//@flow
import React from 'react';
import ReactTable from 'react-table';
import { Box, Columns, Text } from '@locus-taxy/neo-ui';
import { type TFunction } from 'react-i18next';

import theme from './FpPlanDataTable.scss';
import withDraggableColumns from 'react-table-hoc-draggable-columns';
import { type UnallocatedVisitType } from './FpAllocatedVisitsContainer';
import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';
import { PjpPlanStatus } from 'common/models/internal/PermanentJourneyPlan';

const ReactTableDraggableColumns = withDraggableColumns(ReactTable);

type FpPlanViewTableProps = {
    closeMoveVisitDialog: () => void,
    toggleMoveVisitDialog: (visitId: string) => void,
    openMoveVisitDialog: (visitId: string) => void,
    onUnallocatedVisitSelect: (visitId: string) => void,
    onUnallocatedVisitHover: (visitId: string) => void,
    t: TFunction,
    data: Array<UnallocatedVisitType>,
    columns: Array<Object>,
    UnitConverter: UnitConversionHelper,
    paginationDetails: {
        pageSize: number,
        totalItems: number,
        pageSizeOptions: Array<number>,
        totalPages: number,
        selectedPage: number,
        selectedPageIndex: number
    },
    planStatus: $Values<typeof PjpPlanStatus>
};

export default class FpPlanViewTable extends React.PureComponent<FpPlanViewTableProps> {
    rtTable: { current: null | HTMLDivElement };

    noDataComponent = () => {
        return null;
    };

    getTdProps = () => {
        const {
            closeMoveVisitDialog,
            toggleMoveVisitDialog,
            openMoveVisitDialog,
            onUnallocatedVisitSelect,
            onUnallocatedVisitHover,
            planStatus,
            t
        } = this.props;
        return {
            closeMoveVisitDialog,
            toggleMoveVisitDialog,
            openMoveVisitDialog,
            onUnallocatedVisitSelect,
            onUnallocatedVisitHover,
            planStatus,
            t
        };
    };

    _defaultSortMethod = (a, b, desc) => {
        // force null and undefined to the bottom
        let _a = a === null || a === undefined ? -Infinity : a;
        let _b = b === null || b === undefined ? -Infinity : b;
        // force any string values to lowercase
        _a = _a === 'string' ? _a.toLowerCase() : _a;
        _b = _b === 'string' ? _b.toLowerCase() : _b;
        if (_a === 'string' || _b === 'string') {
            if (desc) {
                return _b.localeCompare(_a, 'en', {
                    numeric: true
                });
            }
            return _a.localeCompare(_b, 'en', {
                numeric: true
            });
        }
        // Return either 1 or -1 to indicate a sort priority
        if (_a > _b) {
            return 1;
        }
        if (_a < _b) {
            return -1;
        }
        // returning 0, undefined or any falsey value will use subsequent sorts or the index as a tiebreaker
        return 0;
    };

    _defaultFilterMethod = (filter, row) => {
        const id = filter.pivotId || filter.id;
        return row[id] !== undefined
            ? String(row[id])
                  .toLowerCase()
                  .startsWith(filter.value.toLowerCase())
            : false;
    };

    _getRootStyles = theme => ({
        '.ReactTable .rt-thead .rt-th.rt-resizable-header': {
            paddingRight: '24px'
        },
        '.rt-draggable-container': {
            'div.draggable-header.enable-drag': {
                padding: '2px 8px',
                paddingLeft: '16px'
            }
        },
        '.draggable-header.enable-drag': {
            '&:hover': {
                cursor: '-webkit-grab',
                '&:before': {
                    top: 0,
                    content: '"."',
                    position: 'absolute',
                    left: '8px',
                    fontSize: '20px',
                    lineHeight: '20px',
                    color: 'grey.400',
                    textShadow: `0 5px ${theme.colors.grey['400']},
                                0 10px ${theme.colors.grey['400']},
                                5px 0 ${theme.colors.grey['400']},
                                5px 5px ${theme.colors.grey['400']},
                                5px 10px ${theme.colors.grey['400']}`
                }
            }
        },
        '.arrow': {
            height: '40px',
            width: '2px',
            '&.is-top:before': {
                display: 'inline-block',
                content: '""',
                position: 'absolute',
                height: '42px',
                width: '2px',
                top: '42px',
                left: 0,
                backgroundColor: 'grey.600'
            }
        },
        '.ReactTable .rt-thead .rt-th': {
            '&.-sort-asc, &.-sort-desc': {
                boxShadow: 'none'
            },
            '&:before, &:after': {
                position: 'absolute',
                right: '10px',
                fontSize: '8px'
            },
            '&.rt-resizable-header': {
                '&:before': {
                    content: '"▲"',
                    bottom: '45%'
                },
                '&:after': {
                    content: '"▼"',
                    top: '45%'
                }
            },
            '&.-sort-asc:after': {
                content: '""'
            },
            '&.-sort-desc:before': {
                content: '""'
            }
        }
    });

    render() {
        const { data, columns, t, paginationDetails } = this.props;
        if (data && data.length) {
            return (
                <Box className={theme.containerWrap} sx={this._getRootStyles}>
                    <div className={theme.planTableContainer}>
                        <ReactTableDraggableColumns
                            draggableColumns={{
                                mode: 'reorder',
                                draggable: columns.map(({ id }) => id),
                                enableColumnWideDrag: false
                            }}
                            pageSizeOptions={paginationDetails.pageSizeOptions}
                            showPagination
                            showPaginationTop={false}
                            showPaginationBottom={false}
                            innerRef={this.rtTable}
                            data={data}
                            getTdProps={this.getTdProps}
                            columns={columns}
                            sortable
                            filterable
                            NoDataComponent={this.noDataComponent}
                            page={paginationDetails.selectedPage - 1}
                            pageSize={paginationDetails.pageSize}
                            defaultSortMethod={this._defaultSortMethod}
                            defaultFilterMethod={this._defaultFilterMethod}
                        />
                    </div>
                </Box>
            );
        }
        return (
            <Box height="60px" p={2}>
                <Columns height="fluid" alignY="center" align="center">
                    <Text fontWeight={600} fontSize={2}>
                        {t('fpPlanView.noUnallocatedOutlets')}
                    </Text>
                </Columns>
            </Box>
        );
    }
}
